import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import styles from './Login.module.css';
import { startBackgroundLocationTracking } from '../../utils/locationService';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Frontend validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/login', {
        email,
        password,
        remember,
      });

      if (response.data.success) {
        const { user, access_token } = response.data.data;

        // Save info to localStorage
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', access_token);

        console.log('Login successful:', user);

        // Start background location tracking on android
        startBackgroundLocationTracking();

        // Redirect to dashboard
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);

      if (err.response?.data?.errors) {
        // Handle Laravel validation errors
        const validationErrors = err.response.data.errors;
        const firstErrorKey = Object.keys(validationErrors)[0];
        setError(validationErrors[firstErrorKey][0]);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Connection to server failed. Please ensure the backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles['login-page']}>
      <div className={styles['login-card']}>
        {/* ===== Left Branding Panel ===== */}
        <div className={styles['login-left']}>
          <div className={styles['brand-icon']}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
          </div>
          <h1 className={styles['brand-name']}>Hari Om Electricals</h1>
          <p className={styles['brand-tagline']}>
            Powering your business with precision. Manage your inventory and electrical solutions in one place.
          </p>
          <div className={styles['brand-image']}>
            <img
              src="/images/electrical-panel.png"
              alt="Electrical Panel"
            />
          </div>
        </div>

        {/* ===== Right Form Panel ===== */}
        <div className={styles['login-right']}>
          <h2 className={styles['login-title']}>Welcome Back</h2>
          <p className={styles['login-subtitle']}>Login to manage your inventory</p>

          {error && <div className={styles['error-message']}>{error}</div>}

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className={styles['form-group']}>
              <div className={styles['form-label-row']}>
                <label className={styles['form-label']} htmlFor="email">Email or Username</label>
              </div>
              <div className={styles['input-wrapper']}>
                <span className={styles['input-icon']}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <input
                  id="email"
                  className={styles['form-input']}
                  type="text"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>

            {/* Password */}
            <div className={styles['form-group']}>
              <div className={styles['form-label-row']}>
                <label className={styles['form-label']} htmlFor="password">Password</label>
                <a href="/forgot-password" className={styles['forgot-link']}>Forgot password?</a>
              </div>
              <div className={styles['input-wrapper']}>
                <span className={styles['input-icon']}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  id="password"
                  className={styles['form-input']}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className={styles['password-toggle']}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className={styles['remember-row']}>
              <label className={styles['checkbox-container']}>
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span className={styles['checkmark']}></span>
                <span className={styles['remember-label']}>Remember this device</span>
              </label>
            </div>

            {/* Submit */}
            <button type="submit" className={styles['login-btn']} disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
              {!loading && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                  <polyline points="10 17 15 12 10 7" />
                  <line x1="15" y1="12" x2="3" y2="12" />
                </svg>
              )}
            </button>
          </form>

          <hr className={styles['login-divider']} />

          {/* Footer */}
          <div className={styles['login-footer']}>
            <span className={styles['footer-text']}>Don't have an account?</span>
            <button className={styles['contact-btn']} type="button">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
              Contact Admin
            </button>
          </div>

          <p className={styles['authorized-text']}>Authorized Personnel Only</p>
        </div>
      </div>

    </div>
  );
};

export default Login;
